import { useEffect, useState } from "react";
import { ChevronDown, Plus, Trash2, AlertTriangle, Calculator, Home, FileCheck2, CreditCard, PhoneCall } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const KAALI = "காலி மனை";
const SINGLE_TYPES = ["ஓட்டு வீடு", "Sheet வீடு", "கட்ட குத்து வீடு", "Centring வீடு"];
export const HOUSE_TYPES = [...SINGLE_TYPES, "பாதி ஓடு + Centring", "ஓட்டு வீடு + Centring", "Sheet வீடு + Centring", "கட்ட குத்து வீடு + Centring", KAALI];
const MIXED_PARTS = {
  "பாதி ஓடு + Centring": ["ஓட்டு வீடு", "Centring வீடு"],
  "ஓட்டு வீடு + Centring": ["ஓட்டு வீடு", "Centring வீடு"],
  "Sheet வீடு + Centring": ["Sheet வீடு", "Centring வீடு"],
  "கட்ட குத்து வீடு + Centring": ["கட்ட குத்து வீடு", "Centring வீடு"],
};
const YEARS = [["1-5", "1 to 5 years"], ["5-10", "5 to 10 years"], ["10-15", "10 to 15 years"], ["15-20", "15 to 20 years"]];
const RATES = {
  "ஓட்டு வீடு": { "1-5": 700, "5-10": 500, "10-15": 250, "15-20": 0 },
  "Sheet வீடு": { "1-5": 700, "5-10": 500, "10-15": 250, "15-20": 0 },
  "கட்ட குத்து வீடு": { "1-5": 1000, "5-10": 700, "10-15": 500, "15-20": 0 },
  "Centring வீடு": { "1-5": 1500, "5-10": 1000, "10-15": 750, "15-20": 500 },
};
const EMI_TYPES = ["Car Loan", "Personal Loan", "Gold Loan", "Business Loan", "Credit Card Due", "Home Loan", "Other"];
const CENT_SQFT = 435.6;

const LAP_LIKE = ["LAP", "EMPTY LAND", "PURCHASE LOAN", "PURCHASE", "PURCHASE+CONSTRUCTION"];
const normLp = (lp) => (lp || "").trim().toUpperCase().replace(/\s*\+\s*/g, "+");
export const isLap = (loanProduct) => LAP_LIKE.includes(normLp(loanProduct));
export const defaultHouseTypeFor = (loanProduct) => (normLp(loanProduct) === "EMPTY LAND" ? KAALI : "");
export const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const num = (v) => parseFloat(v) || 0;
const isMixed = (t) => !!MIXED_PARTS[t];
const rateOf = (type, years) => (RATES[type] && years ? RATES[type][years] ?? 0 : 0);

export function computeLap(l = {}) {
  let area = 0, building = 0, rateLabel = "";
  if (isMixed(l.house_type)) {
    (l.parts || []).slice(0, 2).forEach((p, i) => {
      const a = num(p?.length) * num(p?.width);
      const r = rateOf(p?.type, l.years);
      area += a; building += a * r;
      if (p?.type && l.years) rateLabel += `${i ? " + " : ""}Part ${i + 1}: ₹${r}/sq ft`;
    });
  } else if (l.house_type && l.house_type !== KAALI) {
    area = num(l.length) * num(l.width);
    const r = rateOf(l.house_type, l.years);
    building = area * r;
    if (l.years) rateLabel = `₹${r}/sq ft`;
  }
  const cent = l.land_unit === "cent";
  const landSqft = cent ? num(l.land_size) * CENT_SQFT : num(l.land_size);
  const landValue = num(l.land_size) * num(l.land_price);
  const totalEmi = l.other_emis === "yes" ? (l.emis || []).reduce((s, e) => s + num(e?.emi), 0) : 0;
  return { built_up_area: area, building_value: building, rate_applied: rateLabel, land_sqft: Math.round(landSqft * 100) / 100,
    land_value: landValue, grand_total: building + landValue, total_emi: totalEmi, ...computeValueCheck(l) };
}

function computeValueCheck(l) {
  const claimed = num(l.claimed_value), guide = num(l.guideline_value);
  let match = null;
  if (claimed && guide) {
    const pct = (Math.abs(claimed - guide) / guide) * 100;
    match = pct <= 20 ? { tone: "green", text: "OK - Value reasonable", pct }
      : pct <= 40 ? { tone: "yellow", text: "Verify - Site visit must", pct }
      : { tone: "red", text: "High Risk - Senior review needed", pct };
  }
  let finalVal = 0, finalNote = "";
  if (l.vv_site_visit === "yes" && isFilled(l.staff_verified_value)) { finalVal = num(l.staff_verified_value); finalNote = "Staff verified value"; }
  else if (match?.tone === "green") { finalVal = claimed; finalNote = "Customer claimed value (within ±20% of guideline)"; }
  else if (guide) { finalVal = guide; finalNote = "Using guideline value - needs senior approval to change"; }
  return { value_match: match, rent_value: num(l.monthly_rent) * 200, final_approved_value: finalVal, final_value_note: finalNote };
}

const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== "";
export const countFilled = (vals) => ({ filled: vals.filter(isFilled).length, total: vals.length });

export function valuationVals(l) {
  const vals = [l.house_type, l.years, l.land_size, l.land_price, l.value_source, l.claimed_value, l.guideline_value, l.vv_site_visit];
  if (isMixed(l.house_type)) [0, 1].forEach(i => { const p = (l.parts || [])[i] || {}; vals.push(p.type, p.length, p.width); });
  else if (l.house_type !== KAALI) vals.push(l.length, l.width);
  if (l.vv_site_visit === "yes") vals.push(l.vv_site_visit_date, l.staff_verified_value);
  return vals;
}

export function detailsVals(l) {
  const vals = [l.address, l.district, l.owner_name, l.joint, l.occupancy, l.road_access];
  if (l.joint === "yes") vals.push(l.co_owner);
  return vals;
}

export function docsVals(l, hideExistingLoan = false) {
  const vals = [l.patta, l.patta_owner, l.ec, l.parent_doc, l.tax_receipt, l.dispute];
  if (l.house_type !== KAALI) vals.push(l.approval);
  if (hideExistingLoan) return vals;
  vals.push(l.existing_loan);
  if (l.existing_loan === "yes") vals.push(l.el_bank, l.el_balance, l.el_emi);
  return vals;
}

export function emiVals(l) {
  const vals = [l.other_emis, l.cc_usage];
  if (l.other_emis === "yes") (l.emis || []).forEach(e => vals.push(e?.type, e?.emi, e?.missed));
  return vals;
}

export function lapProgress(l = {}) {
  const vals = [...valuationVals(l), ...detailsVals(l), ...docsVals(l), ...emiVals(l),
    l.co_app_name, l.co_app_phone, l.site_visit, l.docs_wa, l.call_time, l.decision_maker, l.urgency, l.applied_elsewhere];
  if (l.decision_maker === "Family Member") vals.push(l.dm_name);
  if (l.applied_elsewhere === "yes") vals.push(l.applied_where);
  return countFilled(vals);
}

const selCls = "mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm focus:border-amber-500 focus:outline-none h-11";
export const L = ({ children, req }) => <Label className="text-[10px] uppercase tracking-widest text-slate-400">{children}{req && <span className="text-red-400"> *</span>}</Label>;

export function Sel({ tid, label, value, onChange, options, req }) {
  return (
    <div>
      <L req={req}>{label}</L>
      <select data-testid={`lap-${tid}`} value={value || ""} onChange={e => onChange(e.target.value)} className={selCls}>
        <option value="">Select…</option>
        {options.map(o => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export function Txt({ tid, label, value, onChange, type = "text", placeholder, extra, req }) {
  return (
    <div>
      <L req={req}>{label}</L>
      <Input data-testid={`lap-${tid}`} type={type} value={value ?? ""} placeholder={placeholder}
        onChange={e => onChange(e.target.value)} className="bg-slate-950 border-slate-700 mt-1 h-11" {...extra}/>
    </div>
  );
}

export function YesNo({ tid, label, value, onChange }) {
  return (
    <div>
      <L>{label}</L>
      <div className="mt-1 flex gap-2" data-testid={`lap-${tid}`}>
        {["yes", "no"].map(v => (
          <button key={v} type="button" data-testid={`lap-${tid}-${v}`} onClick={() => onChange(value === v ? "" : v)}
            className={`flex-1 h-11 rounded-md border text-xs font-bold uppercase tracking-widest transition-colors ${value === v
              ? (v === "yes" ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-300" : "bg-red-500/15 border-red-500/50 text-red-300")
              : "bg-slate-950 border-slate-700 text-slate-400 hover:bg-white/5"}`}>{v}</button>
        ))}
      </div>
    </div>
  );
}

export function ReadOut({ tid, label, value, hint, big }) {
  return (
    <div className={`rounded-md border p-3 ${big ? "border-2 border-amber-500/60 bg-amber-500/10 sm:col-span-2" : "border-slate-800 bg-slate-950/60"}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${big ? "text-amber-300" : "text-slate-500"}`}>{label}</div>
      <div data-testid={`lap-${tid}`} className={`font-mono-alt mt-1 ${big ? "font-display text-3xl sm:text-4xl font-bold text-amber-300" : "text-lg text-white"}`}>{value}</div>
      {hint && <div className="text-[11px] text-amber-300/80 mt-1" data-testid={`lap-${tid}-hint`}>{hint}</div>}
    </div>
  );
}

export const TONES = {
  green: "bg-emerald-500/15 border-emerald-500/50 text-emerald-300",
  yellow: "bg-amber-500/15 border-amber-500/50 text-amber-300",
  red: "bg-red-500/15 border-red-500/50 text-red-300",
};

export function Badge({ s, tid }) {
  if (!s) return <span className="text-[11px] text-slate-500" data-testid={tid}>—</span>;
  return <span data-testid={tid} data-tone={s.tone} className={`inline-flex px-3 py-1 rounded-full border text-xs font-bold ${TONES[s.tone]}`}>{s.text}</span>;
}

const Hint = ({ children }) => <div className="text-[11px] text-amber-300/80 mt-1">{children}</div>;

function ValueVerification({ l, set, c }) {
  const visited = l.vv_site_visit === "yes";
  return (
    <>
      <div className="sm:col-span-2 pt-2 border-t border-slate-800 text-[10px] uppercase tracking-widest text-amber-400 font-bold" data-testid="lap-value-verification">Value Verification</div>
      <Sel tid="value-source" label="Value Source" req value={l.value_source} onChange={v => set({ value_source: v })}
        options={["Customer Estimate", "Broker Quote", "Recent Nearby Sale", "Previous Bank Valuation"]}/>
      <Txt tid="claimed-value" label="Customer Claimed Value (₹)" req type="number" value={l.claimed_value} onChange={v => set({ claimed_value: v })}/>
      <div>
        <Txt tid="guideline-value" label="Guideline Value (₹)" req type="number" value={l.guideline_value} onChange={v => set({ guideline_value: v })}/>
        <Hint>Check at tnreginet.gov.in</Hint>
      </div>
      <div>
        <L>Value Match Status</L>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <Badge s={c.value_match} tid="lap-value-match"/>
          {c.value_match && <span className="text-[11px] text-slate-400">{c.value_match.pct.toFixed(1)}% difference</span>}
        </div>
      </div>
      {c.value_match?.tone === "red" && (
        <div data-testid="lap-value-warning" className="sm:col-span-2 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2 text-xs text-red-300 font-semibold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0"/> Claimed value differs significantly from guideline value. Get senior approval before proceeding.
        </div>
      )}
      <div>
        <Txt tid="monthly-rent" label="Monthly Rent (₹, optional)" type="number" value={l.monthly_rent} onChange={v => set({ monthly_rent: v })}/>
        {num(l.monthly_rent) > 0 && <div className="text-[11px] text-emerald-300 mt-1" data-testid="lap-rent-value">Rent x 200 = {inr(c.rent_value)} (rough estimated value)</div>}
      </div>
      <div>
        <Txt tid="nearby-sale" label="Nearby Recent Sale Price (₹, optional)" type="number" value={l.nearby_sale} onChange={v => set({ nearby_sale: v })}/>
        <Hint>Same area, similar property sale price</Hint>
      </div>
      <YesNo tid="vv-site-visit" label="Site Visit Done?" value={l.vv_site_visit} onChange={v => set({ vv_site_visit: v })}/>
      {visited && <Txt tid="vv-site-visit-date" label="Site Visit Date" type="date" value={l.vv_site_visit_date} onChange={v => set({ vv_site_visit_date: v })}/>}
      <div>
        <Txt tid="staff-verified-value" label="Staff Verified Value (₹)" type="number" value={l.staff_verified_value} extra={{ disabled: !visited }}
          placeholder={visited ? "" : "Mark Site Visit Done first"} onChange={v => set({ staff_verified_value: v })}/>
        <Hint>Fill after site visit inspection</Hint>
      </div>
      <ReadOut tid="final-approved-value" big label="Final Approved Value" value={inr(c.final_approved_value)} hint={c.final_value_note}/>
    </>
  );
}

export function Section({ id, title, icon: Icon, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40 overflow-hidden" data-testid={`lap-section-${id}`}>
      <button type="button" onClick={() => setOpen(o => !o)} data-testid={`lap-section-${id}-toggle`}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors">
        <span className="flex items-center gap-2 font-heading text-sm text-white"><Icon className="w-4 h-4 text-amber-400"/>{title}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && <div className="p-4 border-t border-slate-800 grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>}
    </div>
  );
}

function UnitToggle({ value, onChange }) {
  return (
    <div className="flex gap-1 mt-1" data-testid="lap-land-unit">
      {[["sqft", "Sq Ft"], ["cent", "Cent"]].map(([v, t]) => (
        <button key={v} type="button" data-testid={`lap-land-unit-${v}`} onClick={() => onChange(v)}
          className={`px-3 h-8 rounded-full text-[11px] font-bold border transition-colors ${(value || "sqft") === v ? "bg-amber-500 text-slate-900 border-amber-400" : "border-slate-700 text-slate-300 hover:bg-white/5"}`}>{t}</button>
      ))}
    </div>
  );
}

function MixedPart({ i, part, onChange, years }) {
  const set = (k, v) => onChange({ ...part, [k]: v });
  const area = num(part.length) * num(part.width);
  return (
    <div className="sm:col-span-2 rounded-md border border-amber-500/20 bg-amber-500/5 p-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`lap-part-${i}`}>
      <Sel tid={`part${i}-type`} label={`Part ${i + 1} House Type`} value={part.type} onChange={v => set("type", v)} options={SINGLE_TYPES}/>
      <Txt tid={`part${i}-length`} label="Length (ft)" type="number" value={part.length} onChange={v => set("length", v)}/>
      <Txt tid={`part${i}-width`} label="Width (ft)" type="number" value={part.width} onChange={v => set("width", v)}/>
      <div className="sm:col-span-3 text-[11px] text-slate-400" data-testid={`lap-part${i}-summary`}>
        {area.toLocaleString("en-IN")} sq ft × ₹{rateOf(part.type, years)}/sq ft = <b className="text-amber-300">{inr(area * rateOf(part.type, years))}</b>
      </div>
    </div>
  );
}

export function ValuationSection({ l, set }) {
  const c = computeLap(l);
  const mixed = isMixed(l.house_type);
  const cent = l.land_unit === "cent";
  const pickType = (t) => {
    const parts = MIXED_PARTS[t] ? MIXED_PARTS[t].map((pt, i) => ({ ...((l.parts || [])[i] || {}), type: pt })) : l.parts;
    set({ house_type: t, parts });
  };
  const setPart = (i, p) => { const parts = [...(l.parts || [{}, {}])]; parts[i] = p; set({ parts }); };
  return (
    <Section id="valuation" title="Property Valuation (Calculator)" icon={Calculator}>
      <Sel tid="house-type" label="House Type" req value={l.house_type} onChange={pickType} options={HOUSE_TYPES}/>
      <Sel tid="years" label="Years Since Construction" req value={l.years} onChange={v => set({ years: v })} options={YEARS}/>
      {mixed && [0, 1].map(i => <MixedPart key={i} i={i} part={(l.parts || [])[i] || {}} years={l.years} onChange={p => setPart(i, p)}/>)}
      {!mixed && l.house_type !== KAALI && <>
        <Txt tid="length" label="House Length (ft)" type="number" value={l.length} onChange={v => set({ length: v })}/>
        <Txt tid="width" label="House Width (ft)" type="number" value={l.width} onChange={v => set({ width: v })}/>
      </>}
      {l.house_type !== KAALI && <>
        <ReadOut tid="built-up-area" label="Total Built-up Area" value={`${c.built_up_area.toLocaleString("en-IN")} sq ft`}/>
        <ReadOut tid="building-value" label="Building Value" value={inr(c.building_value)} hint={c.rate_applied ? `Rate applied: ${c.rate_applied}` : "Pick house type & years to apply rate"}/>
      </>}
      <div>
        <div className="flex items-center justify-between"><L>Land Size</L><UnitToggle value={l.land_unit} onChange={v => set({ land_unit: v })}/></div>
        <Input data-testid="lap-land-size" type="number" value={l.land_size ?? ""} onChange={e => set({ land_size: e.target.value })}
          placeholder={cent ? "Cents" : "Sq ft"} className="bg-slate-950 border-slate-700 mt-1 h-11"/>
        {cent && <div className="text-[11px] text-slate-400 mt-1" data-testid="lap-land-sqft">= {c.land_sqft.toLocaleString("en-IN")} sq ft</div>}
      </div>
      <Txt tid="land-price" label={`Current Land Price (₹ per ${cent ? "Cent" : "Sq Ft"})`} type="number" value={l.land_price} onChange={v => set({ land_price: v })}/>
      <ReadOut tid="land-value" label="Total Land Value" value={inr(c.land_value)}/>
      <ReadOut tid="grand-total" big label="Grand Total Property Value" value={inr(c.grand_total)}/>
      <ValueVerification l={l} set={set} c={c}/>
    </Section>
  );
}

export function DetailsSection({ l, set, districts, extra }) {
  return (
    <Section id="details" title="Property Details" icon={Home}>
      <div className="sm:col-span-2">
        <L>Property Full Address</L>
        <textarea data-testid="lap-address" rows={2} value={l.address || ""} onChange={e => set({ address: e.target.value })}
          className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"/>
      </div>
      <Sel tid="district" label="District" value={l.district} onChange={v => set({ district: v })} options={districts.map(d => d.name.trim().toUpperCase())}/>
      <Txt tid="owner-name" label="Property Owner Name" value={l.owner_name} onChange={v => set({ owner_name: v })}/>
      <YesNo tid="joint" label="Joint Ownership?" value={l.joint} onChange={v => set({ joint: v })}/>
      {l.joint === "yes" && <Txt tid="co-owner" label="Co-owner Name" value={l.co_owner} onChange={v => set({ co_owner: v })}/>}
      <Sel tid="occupancy" label="Who stays in property now?" value={l.occupancy} onChange={v => set({ occupancy: v })} options={["Self", "Tenant", "Vacant"]}/>
      <Sel tid="road-access" label="Road access to property" value={l.road_access} onChange={v => set({ road_access: v })} options={["Own Road", "Common Pathway"]}/>
      {extra}
    </Section>
  );
}

export function DocsSection({ l, set, hideExistingLoan }) {
  return (
    <Section id="documents" title="Property Documents" icon={FileCheck2}>
      <YesNo tid="patta" label="Patta Available?" value={l.patta} onChange={v => set({ patta: v })}/>
      <YesNo tid="patta-owner" label="Patta in Owner Name?" value={l.patta_owner} onChange={v => set({ patta_owner: v })}/>
      <YesNo tid="ec" label="EC (Encumbrance Certificate) Available?" value={l.ec} onChange={v => set({ ec: v })}/>
      <YesNo tid="parent-doc" label="Parent Document (Moolapathiram) Available?" value={l.parent_doc} onChange={v => set({ parent_doc: v })}/>
      {l.house_type !== KAALI && <YesNo tid="approval" label="Building Approval / Plan Available?" value={l.approval} onChange={v => set({ approval: v })}/>}
      <YesNo tid="tax-receipt" label="Latest Property Tax Receipt Available?" value={l.tax_receipt} onChange={v => set({ tax_receipt: v })}/>
      <div className="sm:col-span-2">
        <YesNo tid="dispute" label="Any Court Case / Family Dispute on Property?" value={l.dispute} onChange={v => set({ dispute: v })}/>
        {l.dispute === "yes" && (
          <div data-testid="lap-dispute-warning" className="mt-2 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2 text-xs text-red-300 font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4"/> Bank may reject - verify carefully
          </div>
        )}
      </div>
      {!hideExistingLoan && <YesNo tid="existing-loan" label="Existing Loan on This Property?" value={l.existing_loan} onChange={v => set({ existing_loan: v })}/>}
      {!hideExistingLoan && l.existing_loan === "yes" && <>
        <Txt tid="el-bank" label="Bank Name" value={l.el_bank} onChange={v => set({ el_bank: v })}/>
        <Txt tid="el-balance" label="Outstanding Balance (₹)" type="number" value={l.el_balance} onChange={v => set({ el_balance: v })}/>
        <Txt tid="el-emi" label="Monthly EMI (₹)" type="number" value={l.el_emi} onChange={v => set({ el_emi: v })}/>
      </>}
    </Section>
  );
}

function EmiRow({ i, row, onChange, onRemove }) {
  const s = (k, v) => onChange({ ...row, [k]: v });
  return (
    <div className="sm:col-span-2 rounded-md border border-slate-800 bg-slate-950/60 p-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end" data-testid={`lap-emi-row-${i}`}>
      <Sel tid={`emi${i}-type`} label="Loan Type" value={row.type} onChange={v => s("type", v)} options={EMI_TYPES}/>
      <Txt tid={`emi${i}-amount`} label="Monthly EMI (₹)" type="number" value={row.emi} onChange={v => s("emi", v)}/>
      <YesNo tid={`emi${i}-missed`} label="Missed/Bounced (6 mo)?" value={row.missed} onChange={v => s("missed", v)}/>
      <button type="button" onClick={onRemove} data-testid={`lap-emi${i}-remove`} className="h-11 w-11 rounded-md border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center justify-center">
        <Trash2 className="w-4 h-4"/>
      </button>
    </div>
  );
}

export function EmiSection({ l, set }) {
  const rows = l.emis || [];
  const setRow = (i, r) => set({ emis: rows.map((x, j) => (j === i ? r : x)) });
  return (
    <Section id="emis" title="Other EMIs Running" icon={CreditCard}>
      <YesNo tid="other-emis" label="Other EMIs Running?" value={l.other_emis}
        onChange={v => set({ other_emis: v, emis: v === "yes" && !rows.length ? [{}] : rows })}/>
      <Sel tid="cc-usage" label="Credit Card Usage" value={l.cc_usage} onChange={v => set({ cc_usage: v })}
        options={["Below 30% of limit", "30-70%", "Above 70%", "No credit card"]}/>
      {l.other_emis === "yes" && <>
        {rows.map((r, i) => <EmiRow key={i} i={i} row={r || {}} onChange={x => setRow(i, x)} onRemove={() => set({ emis: rows.filter((_, j) => j !== i) })}/>)}
        <button type="button" data-testid="lap-emi-add" onClick={() => set({ emis: [...rows, {}] })}
          className="h-10 rounded-full border border-dashed border-amber-500/50 text-amber-300 text-xs font-bold hover:bg-amber-500/10 flex items-center justify-center gap-2">
          <Plus className="w-4 h-4"/> Add More
        </button>
        <ReadOut tid="total-emi" label="Total Monthly EMI" value={inr(computeLap(l).total_emi)}/>
      </>}
    </Section>
  );
}

function ContactSection({ l, set }) {
  const phoneBad = l.co_app_phone && !/^\d{10}$/.test(l.co_app_phone);
  return (
    <Section id="contact" title="Contact & Follow-up" icon={PhoneCall}>
      <Txt tid="co-app-name" label="Co-applicant Name" value={l.co_app_name} onChange={v => set({ co_app_name: v })}/>
      <div>
        <Txt tid="co-app-phone" label="Co-applicant Phone" value={l.co_app_phone} placeholder="10 digits" extra={{ inputMode: "numeric", maxLength: 10 }}
          onChange={v => set({ co_app_phone: v.replace(/\D/g, "").slice(0, 10) })}/>
        {phoneBad && <div className="text-[11px] text-red-400 mt-1" data-testid="lap-co-app-phone-error">Enter a valid 10-digit phone number</div>}
      </div>
      <Txt tid="site-visit" label="Site Visit Date" type="date" value={l.site_visit} onChange={v => set({ site_visit: v })}/>
      <YesNo tid="docs-wa" label="Documents Received on WhatsApp?" value={l.docs_wa} onChange={v => set({ docs_wa: v })}/>
      <Sel tid="call-time" label="Convenient Call Time" value={l.call_time} onChange={v => set({ call_time: v })} options={["Morning", "Afternoon", "Evening"]}/>
      <Sel tid="decision-maker" label="Decision Maker" value={l.decision_maker} onChange={v => set({ decision_maker: v })} options={["Self", "Family Member"]}/>
      {l.decision_maker === "Family Member" && <Txt tid="dm-name" label="Decision Maker Name" value={l.dm_name} onChange={v => set({ dm_name: v })}/>}
      <Sel tid="urgency" label="Loan Urgency" value={l.urgency} onChange={v => set({ urgency: v })}
        options={["Within 15 days", "Within 1 month", "Within 3 months", "Just Enquiry"]}/>
      <YesNo tid="applied-elsewhere" label="Applied Elsewhere?" value={l.applied_elsewhere} onChange={v => set({ applied_elsewhere: v })}/>
      {l.applied_elsewhere === "yes" && <Txt tid="applied-where" label="Where" value={l.applied_where} onChange={v => set({ applied_where: v })}/>}
    </Section>
  );
}

export default function LapSections({ value, onChange, districts = [], defaultHouseType = "", title = "LAP" }) {
  const l = value || {};
  const set = (patch) => onChange({ ...l, ...patch });
  useEffect(() => {
    if (defaultHouseType && !l.house_type) {
      onChange({ ...l, house_type: defaultHouseType });
    }
  }, [defaultHouseType]); // eslint-disable-line react-hooks/exhaustive-deps
  const { filled, total } = lapProgress(l);
  const pct = total ? Math.round((filled / total) * 100) : 0;
  return (
    <div className="space-y-3" data-testid="lap-sections">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
        <div className="flex items-center justify-between text-xs">
          <span className="uppercase tracking-widest text-amber-400 font-bold text-[10px]">{title} Property Profile</span>
          <span className="font-mono-alt text-amber-300" data-testid="lap-progress-text">{filled}/{total} fields completed</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-500 to-emerald-400 transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="lap-progress-bar"/>
        </div>
      </div>
      <ValuationSection l={l} set={set}/>
      <DetailsSection l={l} set={set} districts={districts}/>
      <DocsSection l={l} set={set}/>
      <EmiSection l={l} set={set}/>
      <ContactSection l={l} set={set}/>
    </div>
  );
}
